import {
  Component,
  OnInit,
  ViewChild,
  Renderer2,
  ElementRef,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { PercentPipe } from '@angular/common';
import {
  FormGroup,
  FormArray,
  FormControl,
  Validators,
  AbstractControl,
  UntypedFormGroup,
  ValidatorFn
} from '@angular/forms';
import { forkJoin } from 'rxjs';
import { timeout, distinctUntilChanged } from 'rxjs/operators';

import { LegComponent } from '@approot/widgets';
import { Utilities, DateUtilities } from '@approot/utilities';
import {
  Entity,
  ScheduleReports,
  ScheduledReport,
  ScheduledReportDropDownOptions,
  KeyValue,
  RequestStatus,
  ReportType,
  ParameterOption,
  ReportingCurrencyStateKey
} from '@approot/models';
import {
  AdminService,
  TradesCaptureService,
  LegService,
  ProductsConfigService,
  UserStorageService
} from '@approot/services';
import ScheduleReportDropDownOptions from './report-dropdown-options.json';

@Component({
  selector: 'app-schedule-reports',
  templateUrl: './schedule-reports.component.html',
  styleUrls: ['./schedule-reports.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class scheduleReportsComponent implements OnInit {
  @ViewChild('divToScroll', { static: false }) public divToScroll: ElementRef;
  @Input() public legComponents: LegComponent[];
  public allCurrencyPairs: string[] = [];
  public selectedReportIndex: number = null;
  public reportsForm: FormGroup;
  public loadingReports = true;
  public teamMembersEmails: string[] = [];
  public dropdownOptions: ScheduledReportDropDownOptions =
    ScheduleReportDropDownOptions;
  public parametersFormGroupForModal: FormGroup;
  public parametersToViewInModal = [];
  public reportTypeOptions: string[] = [];
  public showParameterModal = false;
  public numberOfRunNowSelected = 0;
  public marketDataLive = true; // stores whether live market  data is to be used
  public runReportNowStatus = '';
  public parameterOutputOptionsForModal = [];
  public identifierToLoad: string;
  public savingReportsStatus: RequestStatus = '';
  private entities: string[] = [];

  constructor(
    private renderer: Renderer2,
    private tradesCaptureService: TradesCaptureService,
    private legService: LegService,
    private adminService: AdminService,
    private percentPipe: PercentPipe,
    private changeRef: ChangeDetectorRef,
    private productsConfigService: ProductsConfigService,
    private userStorageService: UserStorageService
  ) {}

  ngOnInit() {
    this.reportsForm = new FormGroup({
      reportsArray: new FormArray([]),
      marketData: new FormControl('', [this.runNowDateValidator.bind(this)]),
      marketDataTime: new FormControl('', [Validators.required]),
      includeTrades: new FormControl(false)
    });

    this.reportsForm.get('marketData').disable(); // default is live so disable it

    this.reportsForm.get('marketData').valueChanges.subscribe(val => {
      DateUtilities.onDateChanged(this.reportsForm.get('marketData')); // when date changes format it
    });

    this.teamMembersEmails = this.adminService.teamMembers.map(
      member => member.email
    );
    this.teamMembersEmails.push(this.adminService.userInfo.email);

    this.reportTypeOptions = this.dropdownOptions.reportTypes.map(
      report => report.longName
    );

    this.allCurrencyPairs =
      this.productsConfigService.cutOffTimesAndFixingSources.currencyPairInfo.map(
        x => {
          return x.currencyPair;
        }
      );

    this.tradesCaptureService
      .getUserInfoRequest({
        task: 'loadUserScheduledReports'
      })
      .subscribe({
        next: (scheduledReports: ScheduleReports) => {
          if (scheduledReports) {
            scheduledReports.reports.forEach((report: ScheduledReport) => {
              // run through the loaded reports and make form controls
              const group = this.formGroupCreator(report); // make the controls
              (this.reportsForm.get('reportsArray') as FormArray).push(group);
              // run now is always disable on load so make it disabled
            });
          }
          this.loadingReports = false; // we have finished loading the reports
          this.changeRef.markForCheck();
        },
        error: () => {
          this.loadingReports = false; // we have finished loading the reports
          this.changeRef.markForCheck();
        }
      });

    this.tradesCaptureService.getEntities().subscribe((entities: Entity[]) => {
      if (entities?.length) {
        this.updateLocalEntities(entities); // since we are downloading the entities update in on the app.
      }
    });
  }

  loadReportModalStatusChanged() {
    this.identifierToLoad = null;
  }
  formGroupCreator(report: ScheduledReport) {
    // creates the group form
    const group = new FormGroup({});

    group.addControl(
      'reportType',
      new FormControl(report && report.reportType ? report.reportType : null, [
        Validators.required
      ])
    );

    let reportType;

    if (report)
      reportType = this.dropdownOptions.reportTypes.find(
        reportTypes => reportTypes.longName === report.reportType
      );
    group.addControl('parameters', this.parametersSetter(reportType, report)); // set the parameters

    group.addControl(
      'identifier',
      new FormControl(report && report.identifier ? report.identifier : '', [
        Validators.required,
        this.identifierValidator.bind(this)
      ])
    );

    group.addControl(
      'sender',
      new FormControl(report && report.sender ? report.sender : '', [
        Validators.required
      ])
    );

    group.addControl(
      'recipients',
      new FormControl(report && report.recipients ? report.recipients : null, [
        Validators.required
      ])
    );

    group.addControl(
      'frequency',
      new FormControl(report && report.frequency ? report.frequency : null, [
        Validators.required
      ])
    );

    group.addControl(
      'time',
      new FormControl(report && report.time ? report.time : null, [
        Validators.required
      ])
    );

    group.addControl(
      'timezone',
      new FormControl(report && report.timezone ? report.timezone : null, [
        Validators.required
      ])
    );

    group.addControl(
      'marketDataTime',
      new FormControl(
        report && report.marketDataTime ? report.marketDataTime : null,
        [Validators.required]
      )
    );

    group.addControl(
      'active',
      new FormControl(
        report && report.active ? report.active === 'True' : false
      )
    );

    group.addControl(
      'runNow',
      new FormControl(report && report.runNow ? report.runNow : false)
    );

    setTimeout(() => {
      group.valid
        ? group.get('runNow').enable()
        : group.get('runNow').disable();
    });

    group.statusChanges.pipe(distinctUntilChanged()).subscribe(val => {
      // listen to status changes to enable and disable the 'run now' checkbox
      this.checkBoxGroupDisablerOnChange(group);
    });
    return group;
  }

  checkBoxGroupDisablerOnChange(group: FormGroup) {
    this.checkBoxGroupDisabler(group);
  }

  checkBoxGroupDisabler(group: FormGroup) {
    if (group.valid) {
      // if the row is now valid (it wasn't before)
      if (group.get('runNow').value) {
        // mark it as selected
        this.numberOfRunNowSelected++;
      }

      group.get('runNow').enable({ emitEvent: false }); // enable the button
    } else {
      // if it is now invalid
      if (group.get('runNow').value) {
        this.numberOfRunNowSelected--; // mark it as invalid
      }
      group.get('runNow').disable({ emitEvent: false }); // disable the checkbox
    }
  }

  parametersSetter(
    reportOptions: ReportType,
    currentValues: ScheduledReport,
    parentName?: string
  ) {
    // sets the parameters based on the reportType
    let parametersGroup = new UntypedFormGroup({});
    if (reportOptions) {
      if (reportOptions.outputParameters) {
        //add outputParameters to the form group
        let outputArray = new FormArray([]);
        const outputs: { type: string; underlying: string }[] =
          (currentValues?.task?.taskParameters?.output as any) || [];
        outputs.forEach((element: { type: string; underlying: string }) => {
          const outputFieldformControl = new FormGroup({
            type: new FormControl(element.type ? element.type : '', [
              Validators.required
            ]),
            underlying: new FormControl(
              element.underlying ? element.underlying : ''
            )
          });
          outputFieldformControl
            .get('underlying')
            .setValidators(
              Utilities.outputUnderlyingValidator.bind(
                this,
                reportOptions.outputParameters
              )
            );
          outputArray.push(outputFieldformControl);
        });
        parametersGroup.addControl('output', outputArray);
      }
      reportOptions.parameters.forEach(parameter => {
        const formControlToAdd = this.getFieldFormControl(
          reportOptions,
          currentValues,
          parameter,
          parentName
        );
        if (parameter.type !== 'parameters') {
          formControlToAdd.setValidators(this.getValidators(parameter));
        }
        formControlToAdd.updateValueAndValidity(); // form control validity needs to be updated
        parametersGroup.addControl(parameter.shortName, formControlToAdd);
      });
    }

    if (parametersGroup.get('lag') && parametersGroup.get('fromDate')) {
      // when any member of the changes validate the group to validate multiple field at the same time
      parametersGroup.setValidators([this.groupValidator.bind(this)]);
      parametersGroup.get('lag').valueChanges.subscribe(val => {
        parametersGroup.get('fromDate').setValue('', { emitEvent: false }); // wipe fromDate
      });

      parametersGroup.get('fromDate').valueChanges.subscribe(val => {
        parametersGroup.get('lag').setValue(null, { emitEvent: false }); // wipe the lag
      });
    }
    return parametersGroup;
  }

  formFieldValidity(formControl: FormControl) {
    return !formControl.get('parameters').valid;
  }

  private getFieldFormControl(
    reportOptions: ReportType,
    currentValues: ScheduledReport,
    parameter: ParameterOption,
    parentName: string
  ): AbstractControl {
    const loadedValue = this.loadFieldCurrentValues(
      reportOptions,
      currentValues,
      parameter,
      parentName
    );
    switch (parameter.type) {
      case 'checkbox':
        return new FormControl(loadedValue || false);
      case 'dropdown':
      case 'multi-select':
        return new FormControl(loadedValue ? loadedValue : null);
      case 'parameters':
        return this.parametersSetter(
          parameter as ReportType,
          currentValues,
          reportOptions.shortName
        );
      case 'percent':
        return new FormControl(
          loadedValue
            ? this.percentPipe.transform(loadedValue, '1.0-12').replace(',', '')
            : ''
        );
      case 'number':
      case 'text':
        return new FormControl(loadedValue ? loadedValue : '');
      case 'date':
        return new FormControl(
          loadedValue ? DateUtilities.dateToPrettyDate(loadedValue) : null
        );
      default:
        return null;
    }
  }

  private getValidators(parameter: ParameterOption): ValidatorFn[] {
    // returns array of all validators
    return parameter.validators.map(validator => {
      switch (validator) {
        case 'required':
          return Validators.required;
        case 'biggerThanZeroPercent':
          return this.biggerThanZeroPercent.bind(this);
        case 'min3':
          return Validators.min(3);
        case 'lessThanZeroPercent':
          return this.LessThanZeroPercent.bind(this);
        case 'integer':
          return Validators.pattern('^[0-9]*$');
        case 'max11':
          return Validators.max(11);
        case 'biggerThanOrEqualZero':
          return this.biggerThanOrEqualZeroPercent.bind(this);
        case 'fromDateValidator':
          return this.fromDateValidator;
        case 'numberAnd-d/b/w':
          return Validators.pattern('^[0-9]*[d|b|w]$');
      }
    });
  }

  loadFieldCurrentValues(
    report: any,
    currentValue: any,
    parameter,
    parentName
  ) {
    if (parentName === 'riskMatrix') {
      return currentValue?.task?.taskParameters[report.shortName][
        parameter.shortName
      ];
    } else {
      return currentValue?.[parameter.shortName];
    }
  }

  groupValidator(controlGroup: FormGroup): null {
    if (controlGroup.controls['lag'] && controlGroup.controls['fromDate']) {
      if (
        controlGroup.controls['lag'].value ||
        DateUtilities.isCorrectFullDateFormat(
          controlGroup.controls['fromDate'].value
        )
      ) {
        controlGroup.controls['lag'].setErrors(null);
        setTimeout(() => {
          controlGroup.controls['fromDate'].setErrors(null);
        });
      }
    }
    return null;
  }

  fromDateValidator(control: AbstractControl): KeyValue | null {
    // field is valid if field is of the correct format or lag is present
    if (
      DateUtilities.isCorrectFullDateFormat(control.value) ||
      (control?.parent?.controls &&
        control.parent.controls['lag'] &&
        control.parent.controls['lag'].value)
    ) {
      return null;
    }
    return { invalidEntry: { value: control.value } };
  }

  reportTypeChange(reportForm: FormGroup) {
    // when the report type changes, check if the additional form controls are there
    // remove them then add the new required controls.

    const report = reportForm.get('reportType').value;
    const reportType = this.dropdownOptions.reportTypes.find(
      reportTypes => reportTypes.longName === report
    );

    reportForm.removeControl('parameters');
    reportForm.addControl(
      'parameters',
      this.parametersSetter(reportType, null)
    );
  }

  openParametersModal(reportForm: FormGroup) {
    // when in view mode, put the correct parameters columns in the array
    this.parametersFormGroupForModal = reportForm.get(
      'parameters'
    ) as FormGroup;
    if (reportForm.get('reportType').value) {
      const scheduleRow = this.dropdownOptions.reportTypes.find(
        reportType => reportType.longName === reportForm.get('reportType').value
      );

      this.parametersToViewInModal = JSON.parse(
        JSON.stringify(scheduleRow.parameters)
      );

      this.parameterOutputOptionsForModal = scheduleRow.outputParameters
        ? scheduleRow.outputParameters
        : [];
      // set the options for the sub parameters fields
      [
        ...this.parametersToViewInModal,
        ...this.parameterOutputOptionsForModal
      ].forEach((par: ParameterOption) => {
        switch (par.dynamicOptionsToUse) {
          case 'entities':
            par.options = this.entities;
            break;
          case 'currencyPairs':
            par.options = this.allCurrencyPairs;
            break;
        }
        if (par.parameters) {
          par.parameters.forEach(subPar => {
            switch (subPar.shortName) {
              case 'underlyingType':
                const underlying = par.parameters.find(
                  data => data.shortName === 'underlying'
                );
                if (
                  this.parametersFormGroupForModal
                    .get(par.shortName)
                    .get(subPar.shortName).value === 'FX Vol' ||
                  this.parametersFormGroupForModal
                    .get(par.shortName)
                    .get(subPar.shortName).value === 'FX Spot'
                ) {
                  underlying.options = this.allCurrencyPairs;
                } else {
                  underlying.options =
                    this.dropdownOptions.underlyingTypeIsIrIndex;
                }
            }
          });
        }
      });
    } else {
      this.parametersToViewInModal = [];
    }
    // console.log('Open Parameters Modal', this.showParameterModal, this.parametersToViewInModal);

    this.showParameterModal = true;
  }

  get reportsFormArray() {
    return (this.reportsForm.get('reportsArray') as FormArray)
      .controls as FormGroup[];
  }

  runNow(checked: boolean) {
    // if a button is checked increment the number of run now checked else decrease it.
    if (checked) {
      this.numberOfRunNowSelected++;
    } else {
      this.numberOfRunNowSelected--;
    }
  }

  addNewReport() {
    (this.reportsForm.get('reportsArray') as FormArray).push(
      this.formGroupCreator(null)
    );
    this.updateScroll();
  }

  get reportsArray(): FormArray {
    return this.reportsForm.get('reportsArray') as FormArray;
  }

  viewParameters(item: FormGroup) {
    if (!this.showParameterModal) {
      // when in view mode, put the correct parameters columns in the array
      this.parametersFormGroupForModal = item.get('parameters') as FormGroup;
      if (item.get('reportType').value) {
        this.parametersToViewInModal = this.dropdownOptions.reportTypes.find(
          reportType => reportType.longName === item.get('reportType').value
        ).parameters;
      } else {
        this.parametersToViewInModal = [];
      }
      this.showParameterModal = true;
    }
  }

  loadReport(item: FormGroup) {
    this.identifierToLoad = item.get('identifier').value;
  }

  parametersModalStatusChanged(event: boolean) {
    this.showParameterModal = event; // hide/show parameters modal
    this.parameterOutputOptionsForModal = [];
  }

  formatAxis(axis) {
    if (axis) {
      // this is stored in the taskParameters
      if (axis.maxLowerShift) {
        axis.maxLowerShift = Utilities.percentageToNumber(
          axis.maxLowerShift
        ).toString();
      }
      if (axis.maxUpperShift) {
        axis.maxUpperShift = Utilities.percentageToNumber(
          axis.maxUpperShift
        ).toString();
      }
      return axis;
    }
  }
  // & { reportingCurrency?: string, uuid?: string;},
  reportsPayloadFormatter(
    row: ScheduledReport,
    reportingCurrency: string,
    uuid?: number
  ) {
    // format the data into what the database is expecting
    const parameters = row.parameters;

    if (parameters.fromDate) {
      parameters.fromDate = DateUtilities.prettyDateToDate(parameters.fromDate);
    }
    let reportToPush = {
      ...row,
      ...parameters
    };

    if (reportToPush.fromDate !== undefined && reportToPush.lag !== undefined) {
      // only one needs to be saved
      if (reportToPush.fromDate) {
        delete reportToPush.lag;
      } else {
        delete reportToPush.fromDate;
      }
    }
    delete reportToPush.parameters;
    delete reportToPush.runNow;

    if (reportToPush.highestVolShift) {
      // turn 5% => 0.05
      reportToPush.highestVolShift = Utilities.percentageToNumber(
        reportToPush.highestVolShift
      ).toString();
    }

    if (reportToPush.lowestVolShift) {
      reportToPush.lowestVolShift = Utilities.percentageToNumber(
        reportToPush.lowestVolShift
      ).toString();
    }

    if (reportToPush.width) {
      reportToPush.width = Utilities.percentageToNumber(
        reportToPush.width
      ).toString();
    }

    if (reportToPush.counterParty === null) {
      delete reportToPush.counterParty;
    }

    if (reportToPush.numpts) {
      reportToPush.numpts = reportToPush.numpts.toString(); // make a string
    }

    reportToPush.active = reportToPush.active ? 'True' : 'False'; // booleans stored as strings

    reportToPush.reportingCurrency = reportingCurrency // store the reporting currency
      ? reportingCurrency
      : 'USD';

    const reportTypeParameters = this.dropdownOptions.reportTypes.find(
      report => report.longName === row.reportType
    );

    if (reportTypeParameters.shortName === 'riskMatrix') {
      // risk matrixs are processed differently
      let taskParameters = {}; // task parameters are stored here

      if (row.parameters['x-axis']) {
        taskParameters['x-axis'] = this.formatAxis(row.parameters['x-axis']);
        delete reportToPush['x-axis'];
      }

      if (row.parameters['y-axis']) {
        taskParameters['y-axis'] = this.formatAxis(row.parameters['y-axis']);
        delete reportToPush['y-axis'];
      }

      if (row.parameters.output) {
        taskParameters['output'] = row.parameters.output.map(outputRow => {
          if (
            outputRow.underlying === null ||
            outputRow.underlying === undefined ||
            outputRow.underlying === ''
          ) {
            delete outputRow.underlying;
          }
          return outputRow;
        });
        delete reportToPush.output; // remove this from the pain object
      }

      const task = {
        taskType: reportTypeParameters.shortName,
        taskParameters: taskParameters
      };
      reportToPush.task = task; // store the task field
    }

    if (uuid) {
      reportToPush.uuid = uuid.toString(); // add the uuid if its needed
    }

    return reportToPush;
  }

  runReportsNow() {
    // when the reports are to run now
    // this.showRunNowSpinner = true; // show spinner
    this.runReportNowStatus = 'sending';
    const reportingCurrency = this.userStorageService.getItem<string>(
      ReportingCurrencyStateKey
    ); // get the stored reporting currency
    const reportRows = this.reportsArray.value.filter(report => report.runNow);
    let runNowRequest = [];
    const timestamp = this.marketDataLive
      ? 'Live'
      : DateUtilities.getDateInISOFormat(
          this.reportsForm.get('marketData').value
        );

    let onScreenProducts = [];
    if (this.reportsForm.get('includeTrades').value) {
      this.legComponents // go through legs and only include those that are checked and the leg is valid
        .forEach(legComponent => {
          if (legComponent.leg.checked && legComponent.form.valid) {
            onScreenProducts.push(
              ...this.legService.createProductsObject([
                {
                  product: legComponent.form.value,
                  attributes: legComponent.leg.attributes
                }
              ])
            );
          }
        });
    }
    reportRows.forEach((row: ScheduledReport) => {
      // make run now requests for each report selected
      const reportToRequest = this.reportsPayloadFormatter(
        JSON.parse(JSON.stringify(row)),
        reportingCurrency
      );

      reportToRequest.timestamp = timestamp;

      if (timestamp === 'Live') {
        delete reportToRequest.marketDataTime;
      } else {
        reportToRequest.marketDataTime =
          this.reportsForm.get('marketDataTime').value;
      }
      delete reportToRequest.frequency;
      delete reportToRequest.time;
      delete reportToRequest.timezone;
      delete reportToRequest.active;

      if (this.reportsForm.get('includeTrades').value) {
        // if need to include all trades include them
        reportToRequest.products = onScreenProducts;
      }

      runNowRequest.push(
        this.tradesCaptureService.runReportNow(reportToRequest)
      );
    });
    forkJoin(runNowRequest)
      .pipe(
        timeout(5_000) // no response from server so end after a few seconds
      )
      .subscribe(
        () => {},
        err => {
          if (err.name === 'TimeoutError') {
            // this is due to our timeout
            this.runReportNowStatus = 'sent';
            setTimeout(() => {
              this.runReportNowStatus = '';
            }, 1500);
          } else {
            this.runReportNowStatus = '';
          }
        }
      );
  }

  saveChanges() {
    const currentTime = new Date().getTime(); // this is used to create the uuid
    const reportingCurrency = this.userStorageService.getItem<string>(
      ReportingCurrencyStateKey
    ); // get the stored reporting currency
    const reportRows = this.reportsArray.value;

    let reports = []; // all reports to be sent are stored here

    reportRows.forEach((row: ScheduledReport, index: number) => {
      const reportToPush = this.reportsPayloadFormatter(
        JSON.parse(JSON.stringify(row)),
        reportingCurrency,
        currentTime + index
      );
      reports.push(reportToPush);
    });

    this.savingReportsStatus = 'LOADING';
    this.tradesCaptureService
      .getUserInfoRequest({
        task: 'saveUserScheduledReports',
        item: {
          email: this.adminService.userInfo.email,
          reports: reports
        }
      })
      .subscribe({
        next: () => {
          this.reportsForm.markAsPristine(); // mark the form as pristine, to signify that no changes have been made
          this.savingReportsStatus = 'DONE';
          this.changeRef.markForCheck();
          this.resetSavingStatus();
        },
        error: () => {
          this.savingReportsStatus = 'ERROR';
          this.changeRef.markForCheck();
          this.resetSavingStatus();
        }
      });
  }

  resetSavingStatus() {
    setTimeout(() => {
      this.savingReportsStatus = '';
      this.changeRef.markForCheck();
    }, 1500);
  }

  textBoxCaretRestrictionHandler(event, action: string) {
    // used to get the index of the item separating editable parts of text and non editable parts
    const indexLocator = Utilities.getIndexOfSeparator(event.target.value, '%');
    if (action === 'keydown') {
      Utilities.textBoxKeydownRestrictor(event, indexLocator, this.renderer);
    } else {
      Utilities.textBoxClickRestrictor(event, indexLocator, this.renderer);
    }
  }

  deleteReport() {
    const reportToDelete = (this.reportsForm.get('reportsArray') as FormArray)
      .controls[this.selectedReportIndex] as FormGroup;

    if (reportToDelete.get('runNow').value) {
      this.numberOfRunNowSelected--; // since deleted, reduce the number of selected run now's by now
    }

    (this.reportsForm.get('reportsArray') as FormArray).removeAt(
      this.selectedReportIndex
    );
    this.reportsArray.markAsDirty(); // mark array as dirty
    this.selectedReportIndex = null; // no row is currently selected
  }

  marketDateChange(isLiveClicked: boolean) {
    // when live or the date textbox is selected
    this.marketDataLive = isLiveClicked;
    if (isLiveClicked) {
      // if live is clicked disabled the text box
      this.reportsForm.get('marketData').disable({ emitEvent: false });
    } else {
      // enable the textbox
      this.reportsForm.get('marketData').enable({ emitEvent: false });
    }
  }

  updateScroll() {
    setTimeout(() => {
      let e = this.divToScroll.nativeElement;
      e.scrollTop = e.scrollHeight;
    }, 0);
  }

  get allowedToSave(): boolean {
    return (
      (this.savingReportsStatus !== 'LOADING' &&
        this.reportsArray.valid &&
        this.reportsArray.dirty) ||
      (this.reportsArray.length === 0 && this.reportsArray.dirty)
    );
  }

  get marketDataValidity() {
    return (
      this.reportsForm.get('marketData').valid &&
      this.reportsForm.get('marketDataTime').valid
    );
  }

  runNowDateValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (control.value && DateUtilities.isPrettyDate(control.value)) {
      const dateObj = new Date(DateUtilities.restructureDate(control.value));

      if (
        new Date() >= dateObj &&
        dateObj.getDay() !== 0 &&
        dateObj.getDay() !== 6
      ) {
        // the date must be in the past or today, and must not be a weekend
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  identifierValidator(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    // validator makes sure that the identifier's are unique
    if (control.parent && control.parent.parent) {
      const previousValue = control.parent.value.identifier;
      const reportsArray = control.parent.parent.controls as FormGroup[];

      let duplicateIdentifiers = 0;

      // find the number of current duplicates and if the value has changed
      // make the duplicates update their validity to see if they are valid now.
      reportsArray.forEach(report => {
        if (
          control.value !== previousValue &&
          report.get('identifier').value === previousValue
        ) {
          report.get('identifier').updateValueAndValidity(); // update the validity of other identifiers that had this
          // controls previous value
        }
        if (report.get('identifier').value === control.value) {
          duplicateIdentifiers++; // duplicate found
        }
      }); // find all id's
      if (duplicateIdentifiers > 1) {
        // if there are 2+ reports that have the same id
        return { invalidEntry: { value: control.value } };
      }
    }
    return null;
  }

  biggerThanOrEqualZeroPercent(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (control.value) {
      const value = control.value.replace('%', '');
      if (+value >= 0) {
        // if value is bigger or equal to 0
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  biggerThanZeroPercent(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (control.value) {
      const value = control.value.replace('%', '');
      if (+value > 0) {
        // if value is bigger than 0
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }

  LessThanZeroPercent(
    control: AbstractControl
  ): { invalidEntry: { value: string } } | null {
    if (control.value) {
      const value = control.value.replace('%', '');
      if (+value < 0) {
        // if value is less than 0
        return null;
      }
    }
    return { invalidEntry: { value: control.value } };
  }
  // percentValidator

  get showScheduleReport() {
    return this.tradesCaptureService.showScheduleReports;
  }

  modalChanged(event: boolean) {
    this.tradesCaptureService.setShowScheduleReports = event;
    // console.log('Schedule Reports, component log');
  }

  private updateLocalEntities(entities: Entity[]) {
    this.entities = this.legService.updateEntities(entities);
  }
}
